import { memo, useCallback, useContext, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import type { CustomFilter, RecordFilter, SourceFilter } from '@trinity/chat';
import { ChatCustomFilter, ChatSource, ChatSourcesMap, extractBrandInsightsIds } from '@trinity/chat';
import { ActiveChatSessionContext, ChatSessionFiltersContext } from '@trinity/chat-components/components/Chat/context';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { GroupFeaturesContext } from '@trinity/chat-components/containers/Chat';
import { CapabilitiesContext } from '../BrandInsights/context';
import { AddCustomFilter } from './CustomFilter.AddButton';
import { CustomFilterForm } from './CustomFilter.Form';
import { CustomFilterValue } from './CustomFilter.Value';
import { DataSource } from './DataSource';
import { Engagement } from './Engagement';
import { Record } from './Record';
import { Source } from './Source';

type Props = {
  active: boolean;
};

export function FilterPaneProfile(props: Props) {

  const dispatch = useContext(ChatStateDispatchContext);
  const [hasUnsavedCustomFilter, setHasUnsavedCustomFilter] = useState(false);
  const capabilities = useContext(CapabilitiesContext);

  const handleAddCustomFilter = useCallback(() => {
    setHasUnsavedCustomFilter(true);
  }, []);

  const handleRemoveUnsavedFilter = useCallback(() => {
    setHasUnsavedCustomFilter(false);
  }, []);

  const handleSaveCustomFilter = useCallback((filter: CustomFilter.Item) => {
    dispatch({
      type: 'filters/add-custom-filter',
      payload: {
        value: filter,
      },
    });
    setHasUnsavedCustomFilter(false);
  }, [dispatch]);

  const groupFeatures = useContext(GroupFeaturesContext);

  const renderCustomFilters = useCallback(() => {
    if (!capabilities.canApplyFilters) {
      return null;
    }

    return (
      <>
        <CustomFiltersController />
        {!hasUnsavedCustomFilter && <AddCustomFilter onClick={handleAddCustomFilter} />}
        {hasUnsavedCustomFilter && (
          <StyledNewCustomFilterForm
            onSubmit={handleSaveCustomFilter}
            onCancel={handleRemoveUnsavedFilter} />
        )}
      </>
    );
  }, [capabilities.canApplyFilters, handleAddCustomFilter, handleRemoveUnsavedFilter, handleSaveCustomFilter, hasUnsavedCustomFilter]);

  return (
    <Root>
      <Wrap>
        <Header>
          <HeaderLabel>Source Filters</HeaderLabel>
          <ResetController {...props} />
        </Header>
        <Filters>
          <DataSourceController />
          <SourceController />
          {groupFeatures.engagementFilter && <EngagementController />}
          <RecordController />
        </Filters>
      </Wrap>
      {renderCustomFilters()}
    </Root>
  );
}

function EngagementController() {
  const filters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);

  return (
    <Engagement
      value={filters.engagement}
      onChange={engagement => {
        dispatch({
          type: 'filters/updated',
          payload: {
            filters: {
              engagement,
            },
          },
        });
      }} />
  );


}

function CustomFiltersController() {
  const filters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);

  const [editing, setEditing] = useState<number>(null);

  const handleRemoveFilter = useCallback((index: number) => () => {
    dispatch({
      type: 'filters/remove-custom-filter',
      payload: {
        id: index,
      },
    });
    setEditing(null);
  }, [dispatch]);

  const handleSaveCustomFilter = useCallback((filter: CustomFilter.Item) => {
    dispatch({
      type: 'filters/update-custom-filter',
      payload: {
        id: editing,
        value: filter,
      },
    });
    setEditing(null);
  }, [dispatch, editing]);

  const handleToggleEdit = useCallback((index: number) => () => {
    setEditing(index);
  }, []);

  const renderCustomFilter = useCallback((item: CustomFilter.Item, index: number) => {
    const isEditing = index === editing;
    if (item.type === ChatCustomFilter.AgentFilter) return null;

    if (isEditing) {
      return (
        <CustomFilterForm
          initialValue={item}
          onSubmit={handleSaveCustomFilter}
          onCancel={() => setEditing(null)} />
      );
    }

    return (
      <CustomFilterValue
        editable={editing === null}
        onEdit={handleToggleEdit(index)}
        onRemove={handleRemoveFilter(index)}
        value={item} />
    );
  }, [editing, handleRemoveFilter, handleSaveCustomFilter, handleToggleEdit]);

  return (
    <>
      {filters.custom.map((m, i) => (
        <CustomFilterRow key={i}>
          {renderCustomFilter(m, i)}
        </CustomFilterRow>
      ))}
    </>
  );
}

function RecordController() {
  const session = useContext(ActiveChatSessionContext);
  const filters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);

  const { context } = session;

  const handleRecordsChange = useCallback((records: RecordFilter.Item[]) => {
    dispatch({
      type: 'filters/updated',
      payload: {
        filters: {
          records,
        },
      },
    });
  }, [dispatch]);

  // Extract project IDs from context for record search
  const projectIds = useMemo(() => {
    if (!context) return [];
    const { projectId } = extractBrandInsightsIds(context);
    return projectId ? [projectId] : [];
  }, [context]);

  return (
    <Record
      value={filters.records ?? []}
      onChange={handleRecordsChange}
      context={context}
      projectIds={projectIds}
    />
  );
}

function DataSourceController() {
  const filters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const { context } = useContext(ActiveChatSessionContext);

  const options = filters.dataSources;

  return (
    <DataSource
      readOnly
      context={context}
      options={options}
      value={filters.dataSources}
      onChange={values => {
        dispatch({
          type: 'filters/updated',
          payload: {
            filters: {
              dataSources: values.length ? values : options,
            },
          },
        });
      }} />
  );
}

function SourceController() {
  const filters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const capabilities = useContext(CapabilitiesContext);

  const options = useMemo(() => {
    return [
      ChatSource.MslInteractions,
      ChatSource.Profiles,
    ].map(o => ({
      id: o,
      name: ChatSourcesMap.get(o),
    }));
  }, []);

  const handleSourceChange = useCallback((sources: SourceFilter.Item[]) => {
    dispatch({
      type: 'filters/updated',
      payload: {
        filters: {
          sources: sources.length ? sources : options,
        },
      },
    });
  }, [dispatch, options]);

  return (
    <Source
      readOnly={!capabilities.canApplyFilters}
      options={options}
      value={filters.sources}
      onChange={handleSourceChange} />
  );
}

const ResetController = memo(({ active }: Props) => {
  const dispatch = useContext(ChatStateDispatchContext);

  return (
    <HeaderReset
      disabled={!active}
      onClick={() => dispatch({ type: 'filters/reset' })}>
      Reset
    </HeaderReset>
  );
});

const basePadding = 12;

const Root = styled.div(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  borderRadius: '5px',
  border: `1px solid ${theme.palette.gray.light1}`,
  boxShadow: '0 2px 6px 2px rgba(60, 64, 67, .15)',
  boxSizing: 'border-box',
  minWidth: 450,
  maxWidth: 600,
}));

 
const Wrap = styled.div(({ theme }) => ({
  padding: basePadding,
  // borderBottom: `1px solid ${theme.palette.gray.light1}`,
}));

const Header = styled.div({
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '10px',
});

const HeaderLabel = styled.h1(({ theme }) => ({
  fontSize: '18px',
  fontFamily: theme.fonts.semiBold,
}));

const HeaderReset = styled.button(({ disabled, theme }) => ({
  color: !disabled ? theme.palette.text.primary : theme.palette.gray.light1,
  fontSize: '14px',
  transition: 'text-decoration 150ms, color 150ms',

  ':hover': {
    textDecoration: disabled ? 'underline' : 'none',
  },
}));

const Filters = styled.div({
  boxSizing: 'border-box',
  padding: '0 2px',
});

const CustomFilterRow = styled.div(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.gray.light1}`,
}));

const StyledNewCustomFilterForm = styled(CustomFilterForm)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.gray.light1}`,
}));

export { FilterPaneWorkspace } from './FilterPane.Workspace';